import { gql } from "@apollo/client"

import { ApiFormattedResponse } from "@models/api_formatted_response"
import { mutate, query } from "@utils/graphql"

/**
 * Hand-written types/documents for a brand-new backend schema addition
 * (`previewAiAnalysisMessages` query + `testAiAnalysis` mutation on the QpienAi
 * module). These are NOT yet in `__generated__/graphql.ts` — once the backend PR
 * ships and admin-panel-v2 reruns `codegen.ts`, replace these local types with the
 * generated ones (names were chosen to match 1:1 so the swap is mechanical).
 */

export type PreviewAiAnalysisAnalysedOn =
  | "OPEN"
  | "OPEN_REOPEN"
  | "CLOSED"
  | "CREATED"
  | "CREATED_REOPENED"
  | "RESOLVED"

export type PreviewAiAnalysisMessagesInput = {
  customerId: string
  conversationId: string
  analyseOn: PreviewAiAnalysisAnalysedOn
}

export type TestAiAnalysisInput = PreviewAiAnalysisMessagesInput

export type PreviewAiAnalysisMessageItem = {
  _id: string | null
  messageType: string | null
  senderType: string | null
  createdAt: string | null
  contentPreview: string | null
  isCommentOrQuestion: boolean | null
  hasReply: boolean | null
}

export type PreviewAiAnalysisPromptTurn = {
  role: string
  content: string
}

export type PreviewAiAnalysisMessagesData = {
  isTicket: boolean
  channelType: string | null
  isAllowedToAnalyze: boolean
  topicsCount: number
  willRunInProduction: boolean
  promptName: string
  channelLabel: string
  selectedMessages: PreviewAiAnalysisMessageItem[]
  formattedPrompt: PreviewAiAnalysisPromptTurn[]
}

export type PreviewAiAnalysisMessagesResponse = {
  success: boolean
  data?: PreviewAiAnalysisMessagesData
  code?: string
  message?: string
}

export type AiAnalysisSentimentData = {
  type: string
  confidence: string
}

export type AiAnalysisTopicData = {
  name: string
  label: string
  confidence: string
}

export type TestAiAnalysisData = {
  reasoning: string | null
  summary: string
  sentiment: AiAnalysisSentimentData
  topics: AiAnalysisTopicData[]
  promptName: string
  channelLabel: string
  model: string
}

export type TestAiAnalysisResponse = {
  success: boolean
  data?: TestAiAnalysisData
  code?: string
  message?: string
}

const PREVIEW_AI_ANALYSIS_MESSAGES = gql`
  query previewAiAnalysisMessages($data: PreviewAiAnalysisMessagesInput!) {
    previewAiAnalysisMessages(data: $data) {
      success
      code
      message
      data {
        isTicket
        channelType
        isAllowedToAnalyze
        topicsCount
        willRunInProduction
        promptName
        channelLabel
        selectedMessages {
          _id
          messageType
          senderType
          createdAt
          contentPreview
          isCommentOrQuestion
          hasReply
        }
        formattedPrompt {
          role
          content
        }
      }
    }
  }
`

const TEST_AI_ANALYSIS = gql`
  mutation testAiAnalysis($data: TestAiAnalysisInput!) {
    testAiAnalysis(data: $data) {
      success
      code
      message
      data {
        reasoning
        summary
        sentiment {
          type
          confidence
        }
        topics {
          name
          label
          confidence
        }
        promptName
        channelLabel
        model
      }
    }
  }
`

export async function previewAiAnalysisMessagesQuery(
  input: PreviewAiAnalysisMessagesInput
): Promise<ApiFormattedResponse<PreviewAiAnalysisMessagesData>> {
  const response = await query<{
    previewAiAnalysisMessages: PreviewAiAnalysisMessagesResponse
  }>({
    query: PREVIEW_AI_ANALYSIS_MESSAGES,
    variables: { data: input },
    fetchPolicy: "no-cache"
  })

  const result = response.data?.previewAiAnalysisMessages
  if (!response.isSuccess || !result?.success) {
    return {
      isSuccess: false,
      errorMessage: result?.message || response.errorMessage || "Preview failed"
    }
  }
  return { isSuccess: true, data: result.data }
}

export async function testAiAnalysisMutation(
  input: TestAiAnalysisInput
): Promise<ApiFormattedResponse<TestAiAnalysisData>> {
  const response = await mutate<{
    testAiAnalysis: TestAiAnalysisResponse
  }>({
    mutation: TEST_AI_ANALYSIS,
    variables: { data: input }
  })

  const result = response.data?.testAiAnalysis
  if (!response.isSuccess || !result?.success) {
    return {
      isSuccess: false,
      errorMessage:
        result?.message || response.errorMessage || "Test analysis failed"
    }
  }
  return { isSuccess: true, data: result.data }
}
